<template>
  <div class="container print-billing">
    <ErrorsMessages :errors="errors" />
    <div v-if="data.id">
      <div class="billing-document">
        <!-- Header com logo e informações da empresa -->
        <div class="document-header">
          <div class="row align-items-center">
            <div class="col-md-2">
              <img
                v-if="rest_home_logo_url"
                class="company-logo"
                :src="rest_home_logo_url"
                alt="Logo da empresa"
              />
            </div>
            <div class="col-md-7">
              <div class="company-info">
                <h4 class="company-name">{{ user.rest_home_name }}</h4>
                <p class="company-address">{{ user.rest_home_address }}</p>
                <p class="company-contact">{{ user.rest_home_first_phone }}</p>
                <p class="company-document">
                  CNPJ: {{ user.rest_home_cnpj }} IE: ISENTO
                </p>
                <p class="company-name-full">
                  {{ user.rest_home_fantasy_name }}
                </p>
              </div>
            </div>
            <div class="col-md-3 text-right">
              <div class="document-info">
                <p class="document-date">{{ formatDate(data.created_at) }}</p>
                <h5 class="document-number">Lançamento {{ data.id }}</h5>
              </div>
            </div>
          </div>
        </div>

        <!-- Informações do Cliente/Residente -->
        <div class="client-section" v-if="data.resident || data.finance_client">
          <div class="client-box">
            <h5 class="client-name">
              {{
                data.resident ? data.resident.name : data.finance_client_name
              }}
            </h5>
            <div class="row">
              <div class="col-md-6">
                <p class="client-document" v-if="data.resident">
                  CPF: {{ data.resident.cpf }}
                  <span v-if="data.resident.rg"
                    >RG: {{ data.resident.rg }}</span
                  >
                </p>
                <p class="client-address" v-if="data.resident">
                  {{ data.resident.address }} - {{ data.resident.city }} -
                  {{ data.resident.state }} - CEP: {{ data.resident.zip_code }}
                </p>
              </div>
              <div class="col-md-6" v-if="data.resident">
                <p class="client-birth">
                  Data de nascimento:
                  {{ formatDate(data.resident.date_of_birth) }}
                  <span v-if="data.resident.age"
                    >({{ data.resident.age }} anos)</span
                  >
                </p>
                <p class="client-admission" v-if="data.resident.admission_date">
                  Data de admissão:
                  {{ formatDate(data.resident.admission_date) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Informações do Lançamento -->
        <div class="launch-info-section">
          <div class="row">
            <div class="col-md-4">
              <div class="info-box">
                <h6 class="info-title">CONTA</h6>
                <p class="info-content">
                  {{ data.finance_configuration_account_name || "" }}
                </p>
              </div>
            </div>
            <div class="col-md-4">
              <div class="info-box">
                <h6 class="info-title">COMPETÊNCIA</h6>
                <p class="info-content">
                  {{ formatDate(data.competency_date || data.due_date, false) }}
                </p>
              </div>
            </div>
            <div class="col-md-4">
              <div class="info-box">
                <h6 class="info-title">VENCIMENTO</h6>
                <p class="info-content">
                  {{ formatDate(data.due_date, false) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Informações de criação -->
        <div class="creation-info" v-if="data.created_by_user">
          <p class="creation-text">
            <strong>Criado em:</strong> {{ formatDate(data.created_at) }}
            <strong>por:</strong> {{ data.created_by_user.name }}
            <span v-if="data.created_by_user.professional_info">
              - {{ data.created_by_user.professional_info }}
            </span>
          </p>
        </div>

        <!-- Título da seção de informações -->
        <div class="section-title">
          <h5><i class="fas fa-info-circle"></i> INFORMAÇÕES DO LANÇAMENTO</h5>
        </div>

        <!-- Tabela de detalhes do lançamento -->
        <div class="launch-details-table">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>VENCIMENTO</th>
                <th>PAGO</th>
                <th>CATEGORIA</th>
                <th>DESCRIÇÃO</th>
                <th>VALOR</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, key) in data.items" :key="key">
                <td>{{ formatDate(item.due_date || data.due_date, false) }}</td>
                <td>
                  {{
                    data.is_paid == 1
                      ? formatDate(data.paid_in_date, false)
                      : "--"
                  }}
                </td>
                <td class="text-uppercase">
                  {{
                    item.finance_configuration_category_name
                      ? item.finance_configuration_category_name
                      : "--"
                  }}
                </td>
                <td class="description-cell">{{ item.description }}</td>
                <td class="text-right">
                  R$ {{ formatMoney(String(item.value)) }}
                </td>
                <td>
                  <span
                    class="status-badge"
                    :class="getStatusClass(data.is_paid)"
                  >
                    <i class="fas fa-check-circle" v-if="data.is_paid == 1"></i>
                    {{ data.is_paid == 1 ? "PAGO" : "NÃO PAGO" }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Total -->
        <div class="total-section">
          <div class="row">
            <div class="col-md-8"></div>
            <div class="col-md-4">
              <div class="total-box">
                <strong>TOTAL: R$ {{ formatMoney(String(data.value)) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="payment-info">
          <div class="row">
            <div class="col-md-6">
              <h6>Condição de pagamento:</h6>
              <p>À vista</p>
            </div>
            <div class="col-md-6">
              <h6>Forma de pagamento:</h6>
              <div
                v-if="exists_finance_configuration_payment_methods_description"
              >
                <p v-for="(item, key) in data.items" :key="key">
                  {{ item.finance_configuration_payment_methods_description }}
                </p>
              </div>
              <p
                v-if="!exists_finance_configuration_payment_methods_description"
              >
                Boleto Bancário
              </p>
            </div>
          </div>
        </div>

        <!-- Botão de impressão -->
        <div class="print-section text-right mt-4">
          <v-btn @click="handlePrint" class="btn-print">
            <i class="fas fa-print mr-2"></i>
            IMPRIMIR
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ErrorsMessages from "../../../../../Components/ErrorsMessages";
import api from "../../../../../../services/api";
import routes from "../../../../../../routes";
import { formatDate, formatMoney } from "../../../../../../lib/format";
import { userData } from "../../../../../../lib/functions";

export default {
  name: "BillingPrint",
  components: {
    ErrorsMessages,
  },
  data() {
    return {
      id: "",
      data: {},
      user: {},
      errors: [],
      rest_home_name: null,
      rest_home_logo_url: null,
      logo: null,
      exists_finance_configuration_payment_methods_description: false,
      loading: true,
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    formatDate,
    formatMoney,

    getStatusClass(is_paid) {
      if (is_paid == 1) {
        return "status-paid";
      }

      return "status-pending";
    },

    async retrieve() {
      const { id } = routes.history.current.params;

      if (!id) {
        this.errors = [{ message: "Dados não encontrado" }];
        return;
      }

      this.id = id;

      const { user, logo } = userData();

      this.user = user;
      this.rest_home_name = user.rest_home_name;
      this.rest_home_logo_url = user.logo_url;
      this.logo = logo;

      await this.get();
    },

    async get() {
      try {
        this.loading = true;

        let { id } = this;
        let params = { id };

        const response = await api.get("/rest-home/financial/launch/receipt", {
          params,
        });

        const { error, errors, data } = response.data;

        if (!error) {
          this.data = data;

          let exists_finance_configuration_payment_methods_description = false;
          for (let item of data.items) {
            if (item.finance_configuration_payment_methods_description) {
              exists_finance_configuration_payment_methods_description = true;
            }
          }

          this.exists_finance_configuration_payment_methods_description =
            exists_finance_configuration_payment_methods_description;
          this.errors = [];
        } else {
          this.errors = errors;
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    handlePrint() {
      window.print();
    },
  },
};
</script>

<style scoped>
.print-billing {
  max-width: 210mm;
  margin: 0 auto;
  padding: 20px;
  background: white;
  color: #000;
  font-family: "WorkSans-Regular", sans-serif;
}

/* Header da empresa */
.document-header {
  border-bottom: 2px solid #ddd;
  padding-bottom: 20px;
  margin-bottom: 30px;
}

.company-logo {
  max-height: 80px;
  max-width: 100%;
  object-fit: contain;
}

.company-info {
  text-align: left;
}

.company-name {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #333;
}

.company-address,
.company-contact,
.company-document,
.company-name-full {
  font-size: 12px;
  margin-bottom: 3px;
  color: #666;
}

.document-info {
  text-align: right;
}

.document-date {
  font-size: 14px;
  margin-bottom: 5px;
}

.document-number {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

/* Seção do cliente */
.client-section {
  margin-bottom: 30px;
}

.client-box {
  border: 2px solid #333;
  padding: 15px;
  border-radius: 4px;
}

.client-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.client-document,
.client-address,
.client-birth,
.client-admission {
  font-size: 12px;
  margin-bottom: 5px;
  color: #333;
}

/* Informações do lançamento */
.launch-info-section {
  margin-bottom: 20px;
}

.info-box {
  margin-bottom: 15px;
}

.info-title {
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.info-content {
  font-size: 14px;
  color: #333;
  margin-bottom: 0;
}

/* Informações de criação */
.creation-info {
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.creation-text {
  font-size: 12px;
  margin-bottom: 0;
  color: #333;
}

/* Título da seção */
.section-title {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #333;
  color: white;
  border-radius: 4px;
}

.section-title h5 {
  margin: 0;
  font-size: 14px;
  font-weight: bold;
}

.section-title i {
  margin-right: 8px;
}

/* Tabela de detalhes */
.launch-details-table {
  margin-bottom: 20px;
}

.launch-details-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.launch-details-table th {
  background-color: #f8f9fa;
  padding: 10px 8px;
  border: 1px solid #ddd;
  font-weight: bold;
  text-align: center;
  font-size: 11px;
}

.launch-details-table td {
  padding: 10px 8px;
  border: 1px solid #ddd;
  vertical-align: middle;
}

.description-cell {
  max-width: 200px;
  word-wrap: break-word;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
}

.status-paid {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-pending {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.status-overdue {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.status-default {
  background-color: #e2e3e5;
  color: #383d41;
  border: 1px solid #d6d8db;
}

.status-badge i {
  margin-right: 4px;
  font-size: 10px;
}

/* Total */
.total-section {
  margin-bottom: 30px;
}

.total-box {
  text-align: right;
  padding: 15px;
  background-color: #f8f9fa;
  border: 2px solid #333;
  border-radius: 4px;
  font-size: 16px;
}

/* Informações de pagamento */
.payment-info {
  margin-bottom: 30px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.payment-info h6 {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #333;
}

.payment-info p {
  font-size: 12px;
  margin-bottom: 10px;
  color: #666;
}

.installments-table table {
  font-size: 11px;
}

.installments-table th,
.installments-table td {
  padding: 6px 8px;
  border: 1px solid #ddd;
}

/* Botão de impressão */
.print-section {
  margin-top: 40px;
  page-break-inside: avoid;
}

.btn-print {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-print:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(245, 158, 11, 0.4) !important;
}

/* Estilos de impressão */
@media print {
  .print-billing {
    margin: 0;
    padding: 15mm;
    box-shadow: none;
  }

  .btn-print,
  .print-section {
    display: none !important;
  }

  .document-header {
    border-bottom: 2px solid #000;
  }

  .client-box {
    border: 2px solid #000;
  }

  .total-box {
    border: 2px solid #000;
  }

  .section-title {
    background-color: #000 !important;
    color: white !important;
  }

  .launch-details-table th {
    background-color: #f0f0f0 !important;
  }

  .launch-details-table table,
  .launch-details-table th,
  .launch-details-table td {
    border: 1px solid #000 !important;
  }

  .installments-table table,
  .installments-table th,
  .installments-table td {
    border: 1px solid #000 !important;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .print-billing {
    padding: 15px;
  }

  .document-header .row {
    flex-direction: column;
    text-align: center;
  }

  .launch-details-table {
    overflow-x: auto;
  }

  .launch-details-table table {
    min-width: 600px;
  }

  .client-box .row {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .print-billing {
    padding: 10px;
  }

  .company-name {
    font-size: 16px;
  }

  .document-number {
    font-size: 14px;
  }

  .launch-details-table th,
  .launch-details-table td {
    padding: 6px 4px;
    font-size: 10px;
  }
}
</style>